import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check } from "lucide-react";
import { HOW_IT_WORKS_STEPS } from "@/lib/how-it-works";
import { VideoScanPromo, PhotoScanPromo, DeepScanPromo } from "@/components/VideoScanPromo";
import { fillSurface, useSurfaceWords } from "@/lib/surface";
import { useExpertFeatures } from "@/hooks/useExpertFeatures";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";
import { localizedPath } from "@/lib/i18n/slug-map";

export const Route = createFileRoute("/sa-fungerar-det")({
  head: () => ({
    meta: [
      { title: "Så fungerar det – från foto till publicerad annons | Sellavo" },
      {
        name: "description",
        content:
          "Fyra steg: fota prylen, få färdig annonstext, få prisförslag och dela annonsen. Så fungerar Sellavo, steg för steg.",
      },
      { property: "og:title", content: "Så fungerar det – från foto på prylen till publicerad annons" },
      {
        property: "og:description",
        content:
          "Fota prylen, få färdig text och prisförslag av oss och publicera annonsen gratis. Här är hela flödet steg för steg.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://sellavo.com/sa-fungerar-det" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/sa-fungerar-det" }],
  }),
  component: HowItWorksPage,
});

export function HowItWorksPage() {
  const words = useSurfaceWords();
  const { enabled: testFeatures } = useExpertFeatures();
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).how;
  const steps = copy ? HOW_IT_WORKS_STEPS.map((step, i) => ({ ...step, title: copy.steps[i][0], description: copy.steps[i][1], detail: copy.steps[i][2] })) : HOW_IT_WORKS_STEPS;
  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <header>
        <h1 className="font-heading text-3xl font-bold text-foreground sm:text-4xl">
          {copy?.title ?? "Så fungerar det – från pryl till publicerad annons"}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {copy?.intro ?? "Fyra steg från foto på prylen till publicerad annons. Du behöver inte skriva en rad själv – men du kan ändra allt vi föreslår, både innan och efter att annonsen ligger uppe."}
        </p>
      </header>

      <ol className="mt-8 grid gap-4 sm:grid-cols-2">
        {steps.map((step, i) => (
          <li
            key={step.title}
            className="h-full rounded-lg border border-border bg-card p-5 shadow-sm"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ocean-100 text-ocean-700">
              <step.icon className="h-4.5 w-4.5" />
            </span>
            <h2 className="mt-3 font-heading text-base font-semibold text-foreground">
              {i + 1}. {step.title}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{step.description}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{fillSurface(step.detail, words)}</p>
          </li>
        ))}
      </ol>

      <section className="mt-8 rounded-lg border border-border bg-muted/40 p-5">
        <h2 className="font-heading text-lg font-semibold text-foreground">
          {copy?.freeTitle ?? "Allt ovanför är gratis"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {copy?.freeText ?? "10 annonser i månaden med upp till 3 bilder – utan kostnad."}
        </p>
        <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
          <li className="flex gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-ocean-500" />
            <span>
              {copy?.plus ?? <><span>Behöver du fler bilder? </span><strong className="font-semibold text-foreground">Plus</strong>{" ger 6 bilder per annons, 60 annonser i månaden och möjlighet att lyfta fram annonser."}</>}
            </span>
          </li>
          <li className="flex gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-ocean-500" />
            <span>
              {copy?.pro ?? <><span>Vill du ladda upp video, skanna hemmet med film eller lägga ut hur många annonser som helst? </span><strong className="font-semibold text-foreground">Pro</strong>{" har allt det."}</>}
            </span>
          </li>
        </ul>
        <Link
          to={localizedPath("/priser", lang)}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          {copy?.plans ?? "Se vad som ingår i varje konto"} <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <VideoScanPromo className="mt-8" />
      <PhotoScanPromo className="mt-4" />
      {testFeatures && <DeepScanPromo className="mt-4" />}
    </main>
  );
}
